import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Row, Col } from 'react-flexbox-grid';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { Sticky } from 'react-sticky';
import { getFormValues } from 'redux-form';
import { findIndex, propEq, length, compose, head, filter, is, complement, keys, forEach, path } from 'ramda';

import { findCurrentlyEditedDoc, getLatestUnfinishedCommit } from 'lib/documentHandling';
import pickSpec from 'lib/pickSpec';
import { editTitle } from 'lib/toolbar';

import ContentWrapper from 'components/ContentWrapper';
import ActionButton from 'components/ActionButton';
import DeleteHandler from 'components/DeleteHandler';

import { selectors } from 'store/newDocumentStore';

import NavigationDropdown from './NavigationDropdown';
import { toolbar, toIndex, crumb, crumbText, toNextSection, fullHeight, buttonArea, crumbButton } from './Toolbar.less';
import navIcon from './assets/to-summary-icon.svg';

import { deletableDocTypes } from '../../../../../project.config.js';

// Finds the next element in the navigation sectio array. If current item is the last item in the array,
// then it gives out the first item.
const findNext = (name, sections) =>
  findIndex(propEq('name', name), sections) === length(sections) - 1
    ? sections[0]
    : sections[findIndex(propEq('name', name), sections) + 1];

const handleCrumbTitle = (title, localeMessage, doc) => {
  if (is(String, title)) return localeMessage(title);
  if (is(Function, title)) return title(localeMessage, doc);
  return '';
};

const filterNavigableSections = filter(complement(propEq('nonNavigable', true)));
const filterControllableSections = filter(propEq('nonControllable', true));
const filterCancellableSections = filter(propEq('nonCancellable', true));

const getViewName = viewstate => {
  let keyArray = keys(viewstate);
  let viewKey = null;
  forEach(k => {
    if (path([k], viewstate)) viewKey = k;
  }, keyArray);
  return viewKey;
};

const getViewingDocument = viewstate => {
  const viewName = getViewName(viewstate);
  return viewName ? viewstate[viewName] : undefined;
};

const defaultSection = pickSpec({ name: null, id: '', viewTitle: 'relapse.viewTitle', editTitle });

const getActiveSection = (sections, name) =>
  sections && name ? compose(defaultSection, head, filter(propEq('name', name)))(sections) : defaultSection({});

const Toolbar = ({
  name,
  navigate,
  sections,
  sectionState,
  cancel,
  done,
  currentFormValues,
  localeMessage,
  clearViewId,
  viewstate,
}) => {
  const thisSectionState = selectors.selectModuleState(name, { modules: sectionState });
  const section = getActiveSection(sections, name);
  const editDoc = thisSectionState ? findCurrentlyEditedDoc(thisSectionState.documents) : null;
  const editedCommit = editDoc ? getLatestUnfinishedCommit(editDoc) : null;
  const viewDoc = getViewingDocument(viewstate);
  const editing = !!editDoc;
  const viewing = !!viewDoc;
  const deleteEnabled = editDoc && deletableDocTypes.includes(editDoc.documentType);
  const navigableSections = filterNavigableSections(sections);
  const noControlsSections = filterControllableSections(sections).map(s => s.name);
  const noControls = noControlsSections.includes(name);
  const noCancellingSections = filterCancellableSections(sections).map(s => s.name);
  const noCancels = noCancellingSections.includes(name);

  return (
    <Sticky>
      {({ style }) => (
        <div style={style} className={toolbar}>
          <ContentWrapper>
            <Grid fluid className={fullHeight}>
              <Row middle="xs" className={fullHeight}>
                <Col xs={12} sm={7} md={6}>
                  <Col className={fullHeight}>
                    <Link to="/">
                      <img alt="" src={navIcon} className={toIndex} />
                    </Link>
                  </Col>
                  <Row middle="xs">
                    <Col>
                      <div className={crumb} children="❯" />
                      <NavigationDropdown
                        navigate={navigate}
                        sections={navigableSections}
                        clickElement={
                          <Row style={{ margin: 0 }}>
                            <div className={crumbText}>
                              <h6>
                                {!viewing &&
                                  handleCrumbTitle(
                                    editing && section.id === 'status.title' ? 'status.header' : section.id,
                                    localeMessage,
                                  )}
                                {viewing && localeMessage(getViewName(viewstate) + '.title')}
                              </h6>
                            </div>
                            <div style={{ float: 'left' }}>
                              {!editing && !viewing && <div className={crumbButton} children="❯" />}
                            </div>
                          </Row>
                        }
                      />
                    </Col>
                    {(editing || viewing) && (
                      <Col>
                        <div className={crumb} children="❯" />
                        <div className={crumbText}>
                          <h6>
                            {editing && handleCrumbTitle(section.editTitle, localeMessage, editDoc)}
                            {!editing && viewing && localeMessage(getViewName(viewstate) + '.viewTitle')}
                          </h6>
                        </div>
                      </Col>
                    )}
                  </Row>
                </Col>
                {!editing && !viewing && (
                  <Col xs={0} sm={5} mdOffset={2} md={4} lgOffset={3} lg={3}>
                    <Link className={toNextSection} to={`${DOCUMENTS_PATH}/${findNext(name, navigableSections).name}`}>
                      <h6>
                        <FormattedMessage id={'toolbar.action.next'} />
                      </h6>
                      <div className={crumb} children="❯" />
                    </Link>
                  </Col>
                )}
                {!noControls && editing && (
                  <Col xs={12} sm={5} mdOffset={2} md={4} lgOffset={2} lg={4} className={buttonArea}>
                    <Row center="xs" end="sm">
                      {deleteEnabled && (
                        <Col xs={1}>
                          <DeleteHandler documentType={name} documentId={editDoc.documentId} />
                        </Col>
                      )}
                      <Col xs={deleteEnabled ? 5 : 6}>
                        {!noCancels && (
                          <ActionButton
                            localeId="toolbar.action.cancel"
                            white
                            onClick={e => cancel(name, editDoc.documentId, editedCommit.commitId)}
                          />
                        )}
                      </Col>
                      <Col xs={6}>
                        <ActionButton
                          localeId="toolbar.action.done"
                          border
                          white
                          onClick={e => done(name, editDoc.documentId, editedCommit.commitId, currentFormValues)}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
                {!noControls && !editing && viewing && (
                  <Col xs={12} sm={5} mdOffset={2} md={4} lgOffset={3} lg={3} className={buttonArea}>
                    <Row center="xs" end="sm">
                      <Col xs>
                        <ActionButton
                          localeId="toolbar.action.closeView"
                          white
                          border
                          onClick={e => clearViewId(getViewName(viewstate))}
                        />
                      </Col>
                    </Row>
                  </Col>
                )}
              </Row>
            </Grid>
          </ContentWrapper>
        </div>
      )}
    </Sticky>
  );
};

Toolbar.propTypes = {
  cancel: PropTypes.func.isRequired,
  done: PropTypes.func.isRequired,
  clearViewId: PropTypes.func.isRequired,
  localeMessage: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  navigate: PropTypes.func,
  sections: PropTypes.array,
  sectionState: PropTypes.object,
  currentFormValues: PropTypes.object,
  viewstate: PropTypes.object,
};

const mapStateToProps = (state, ownProps) => {
  let currentFormValues = null;
  if (ownProps.name && state.form) {
    currentFormValues = getFormValues(ownProps.name)(state);
  }
  return { currentFormValues, viewstate: state.viewstate };
};

export default connect(mapStateToProps)(Toolbar);
